import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, GraduationCap, FileUp, BookOpenText } from "lucide-react";
import { useI18n } from "../lib/i18n";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import SamiTrainingProgress from "../components/SamiTrainingProgress";
import SamiSelfLearnPanel from "../components/SamiSelfLearnPanel";

export default function AdminTraining() {
  const { lang } = useI18n();
  const { user, loading } = useAuth();
  const isAr = lang === "ar";

  const [items, setItems] = useState([]);

  // Single Q&A form
  const [single, setSingle] = useState({ question: "", answer: "", language: "ar", category: "" });
  const [busy, setBusy] = useState(false);

  // Bulk form
  const [bulk, setBulk] = useState({ text: "", language: "ar" });
  const [bulkBusy, setBulkBusy] = useState(false);

  // Auto-answer form (questions only, Sami generates the answers)
  const [auto, setAuto] = useState({ text: "", language: "ar", category: "" });
  const [autoBusy, setAutoBusy] = useState(false);
  const [autoProgress, setAutoProgress] = useState(null); // {done, total}

  // Cross-environment import
  const [importUrl, setImportUrl] = useState("");
  const [importToken, setImportToken] = useState("");
  const [importing, setImporting] = useState(false);

  const t = useMemo(() => ({
    eyebrow: isAr ? "تدريب سامي" : "Train Sami",
    title: isAr
      ? "علّم سامي طريقة الإجابة المثالية."
      : "Teach Sami the firm's preferred answers.",
    lead: isAr
      ? "أضف أسئلة وأجوبة نموذجية. سامي سيستخدمها كمرجع رسمي لأسلوبه ومضمون إجاباته في كلا الوضعين (العامة والموظفين)."
      : "Add curated question/answer pairs. Sami will treat them as the authoritative reference for tone and content in both Public and Staff modes.",
    addOne: isAr ? "إضافة سؤال وجواب" : "Add one Q&A",
    bulkAdd: isAr ? "رفع مجموعة دفعة واحدة" : "Bulk upload",
    library: isAr ? "ما يعرفه سامي حاليًا" : "What Sami currently knows",
    libraryEmpty: isAr ? "لم تتم إضافة أي تدريب بعد." : "No training Q&A added yet.",
    question: isAr ? "السؤال" : "Question",
    answer: isAr ? "الإجابة المثالية" : "Ideal answer",
    category: isAr ? "تصنيف (اختياري)" : "Category (optional)",
    language: isAr ? "اللغة" : "Language",
    save: isAr ? "حفظ" : "Save",
    bulkText: isAr ? "النص" : "Text",
    bulkPh: isAr
      ? "Q: ما مدة الإشعار في عقد العمل؟\nA: مدة الإشعار 30 يومًا...\n---\nQ: متى يحق للعامل المطالبة بمكافأة نهاية الخدمة؟\nA: ..."
      : "Q: What is the notice period under Kuwaiti employment law?\nA: The notice period is 30 days…\n---\nQ: When is end-of-service indemnity due?\nA: …",
    bulkSubmit: isAr ? "رفع جميع الأسئلة" : "Upload all",
    bulkAdded: isAr ? "تمت الإضافة" : "Added",
    autoTitle: isAr ? "أضف الأسئلة فقط — وسامي يجيب عليها" : "Add questions only — Sami will answer",
    autoLead: isAr
      ? "اكتب أسئلتك (سؤال في كل سطر، أو مرقّمة، أو مع علامة Q:). سيقوم سامي بقراءة التشريعات المرفوعة، صياغة الإجابة المثالية لكل سؤال، وحفظها في مكتبة التدريب."
      : "Type only the questions (one per line, numbered, or with Q: prefix). Sami will read the uploaded statutes, craft the ideal answer for each, and save the pair to the training library.",
    autoSubmit: isAr ? "اطلب من سامي الإجابة وحفظها" : "Have Sami answer & save",
    autoCategory: isAr ? "تصنيف اختياري (مثال: personal_status)" : "Optional category (e.g. personal_status)",
    autoBatchInfo: isAr
      ? "8 أسئلة كحد أقصى لكل دفعة. الدفعات الأكبر تُقسَّم تلقائيًا."
      : "Max 8 questions per batch — larger inputs are split automatically.",
    autoProgressLabel: isAr ? "جاري إجابة سامي" : "Sami is answering",
    autoDone: (n) => isAr ? `تم حفظ ${n} زوج (سؤال/جواب).` : `Saved ${n} Q&A pair${n === 1 ? "" : "s"}.`,
    delete: isAr ? "حذف" : "Delete",
    confirmDelete: isAr ? "حذف هذا السؤال؟" : "Delete this Q&A?",
    err: isAr ? "حدث خطأ. حاول مرة أخرى." : "Something went wrong.",
    syncTitle: isAr ? "استيراد التدريب من بيئة أخرى" : "Import training from another environment",
    syncLead: isAr
      ? "ألصق رابط /api/admin/training/export الخاص بـ Preview، ولصق Bearer Token (اختياري للحماية). الأسئلة المكررة (نفس النص) تُتجاوز."
      : "Paste the /api/admin/training/export URL of your Preview deployment + an optional Bearer token. Duplicate questions are skipped.",
    syncPlaceholder: "https://example.com/api/admin/training/export",
    syncTokenPh: isAr ? "Bearer Token من Preview (اختياري)" : "Bearer token from Preview (optional)",
    syncBtn: isAr ? "استيراد الآن" : "Import now",
    syncing: isAr ? "جارٍ الاستيراد…" : "Importing…",
    syncOk: (n) => isAr ? `تم استيراد ${n} سؤال.` : `Imported ${n} Q&A.`,
  }), [isAr]);

  const load = useCallback(() => {
    api.get("/admin/training").then((r) => setItems(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (user?.role === "admin") load();
  }, [user, load]);

  if (loading) return <div className="p-20 text-center text-slate-500 text-sm">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "admin") return <Navigate to="/" replace />;

  const onAddSingle = async (e) => {
    e.preventDefault();
    if (!single.question.trim() || !single.answer.trim()) return;
    setBusy(true);
    try {
      await api.post("/admin/training/add", {
        ...single,
        category: single.category || null,
      });
      toast.success(t.bulkAdded);
      setSingle({ question: "", answer: "", language: single.language, category: "" });
      load();
    } catch {
      toast.error(t.err);
    } finally {
      setBusy(false);
    }
  };

  const onBulk = async (e) => {
    e.preventDefault();
    if (!bulk.text.trim()) return;
    setBulkBusy(true);
    try {
      const { data } = await api.post("/admin/training/bulk", bulk);
      toast.success(`${t.bulkAdded}: ${data.added}`);
      setBulk({ text: "", language: bulk.language });
      load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : t.err);
    } finally {
      setBulkBusy(false);
    }
  };

  const splitQuestions = (text) =>
    text
      .split(/\r?\n+/)
      .map((line) =>
        line
          .replace(/^\s*(?:[0-9٠-٩]+\s*[.\-)]|[-*•]|Q\s*[:.\-]|q\s*[:.\-]|س\s*[:.\-])\s*/, "")
          .trim()
      )
      .filter((q) => q.length >= 6);

  const onAuto = async (e) => {
    e.preventDefault();
    const all = splitQuestions(auto.text);
    if (all.length === 0) {
      toast.error(t.err);
      return;
    }
    setAutoBusy(true);
    setAutoProgress({ done: 0, total: all.length });
    let total = 0;
    try {
      for (let i = 0; i < all.length; i += 8) {
        const batch = all.slice(i, i + 8);
        const { data } = await api.post(
          "/admin/training/auto-answer",
          {
            questions: batch,
            language: auto.language,
            category: auto.category || null,
          },
          { timeout: 180000 }
        );
        total += data.added || 0;
        setAutoProgress({ done: Math.min(i + batch.length, all.length), total: all.length });
        load();
      }
      toast.success(t.autoDone(total));
      setAuto({ text: "", language: auto.language, category: auto.category });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : t.err);
    } finally {
      setAutoBusy(false);
      setAutoProgress(null);
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm(t.confirmDelete)) return;
    try {
      await api.delete(`/admin/training/${id}`);
      load();
    } catch {
      toast.error(t.err);
    }
  };

  const onImport = async (e) => {
    e.preventDefault();
    if (!importUrl.trim()) return;
    setImporting(true);
    try {
      const { data } = await api.post(
        "/admin/training/import-from-url",
        {
          url: importUrl.trim(),
          overwrite: false,
          bearer_token: importToken.trim() || null,
        },
        { timeout: 600000 }  // big dumps may take minutes
      );
      toast.success(t.syncOk(data.inserted ?? 0));
      setImportUrl("");
      setImportToken("");
      load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : t.err);
    } finally {
      setImporting(false);
    }
  };

  return (
    <div data-testid="admin-training-page" className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
      <div className="grid lg:grid-cols-12 gap-12 mb-12">
        <div className="lg:col-span-6">
          <p className="eyebrow">{t.eyebrow}</p>
          <h1 className="font-display text-5xl lg:text-6xl mt-4 leading-[1.0] tracking-tight">
            {t.title}
          </h1>
        </div>
        <div className="lg:col-span-6 lg:pt-3">
          <p className="text-lg text-slate-600 leading-relaxed">{t.lead}</p>
        </div>
      </div>

      <SamiTrainingProgress />

      <SamiSelfLearnPanel />

      <form
        onSubmit={onImport}
        className="border border-slate-200 bg-slate-50 p-5 lg:p-6 mb-8 grid md:grid-cols-[1fr_1fr_auto] gap-3 items-start"
        data-testid="training-import-form"
      >
        <div className="md:col-span-3 mb-1">
          <p className="eyebrow">{t.syncTitle}</p>
          <p className="text-sm text-slate-600 leading-relaxed mt-1">{t.syncLead}</p>
        </div>
        <Input
          type="url"
          value={importUrl}
          onChange={(e) => setImportUrl(e.target.value)}
          placeholder={t.syncPlaceholder}
          data-testid="training-import-url"
          className="rounded-sm h-11"
          dir="ltr"
        />
        <Input
          type="password"
          value={importToken}
          onChange={(e) => setImportToken(e.target.value)}
          placeholder={t.syncTokenPh}
          data-testid="training-import-token"
          className="rounded-sm h-11"
          dir="ltr"
        />
        <Button
          type="submit"
          disabled={importing || !importUrl.trim()}
          data-testid="training-import-submit"
          className="rounded-sm h-11 bg-slate-900 hover:bg-slate-800 text-white whitespace-nowrap"
        >
          {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : t.syncBtn}
        </Button>
      </form>

      <div className="grid lg:grid-cols-12 gap-8">
        {/* SINGLE + BULK */}
        <div className="lg:col-span-5 space-y-8">
          {/* Single Q&A */}
          <form onSubmit={onAddSingle} className="border border-slate-200 bg-white p-6 lg:p-8 space-y-5" data-testid="qa-add-form">
            <div className="flex items-center gap-2">
              <Plus className="w-4 h-4 text-slate-500" />
              <p className="eyebrow">{t.addOne}</p>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.2em] text-slate-500">{t.question}</Label>
              <Textarea
                required dir="auto" rows={2}
                value={single.question}
                onChange={(e) => setSingle({ ...single, question: e.target.value })}
                data-testid="qa-question"
                className="mt-2 rounded-sm"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.2em] text-slate-500">{t.answer}</Label>
              <Textarea
                required dir="auto" rows={6}
                value={single.answer}
                onChange={(e) => setSingle({ ...single, answer: e.target.value })}
                data-testid="qa-answer"
                className="mt-2 rounded-sm"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs uppercase tracking-[0.2em] text-slate-500">{t.language}</Label>
                <Select value={single.language} onValueChange={(v) => setSingle({ ...single, language: v })}>
                  <SelectTrigger data-testid="qa-language" className="mt-2 rounded-sm h-11">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ar">{isAr ? "العربية" : "Arabic"}</SelectItem>
                    <SelectItem value="en">{isAr ? "الإنجليزية" : "English"}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.2em] text-slate-500">{t.category}</Label>
                <Input
                  dir="auto"
                  value={single.category}
                  onChange={(e) => setSingle({ ...single, category: e.target.value })}
                  data-testid="qa-category"
                  className="mt-2 rounded-sm h-11"
                />
              </div>
            </div>
            <Button
              type="submit" disabled={busy}
              data-testid="qa-submit"
              className="rounded-sm h-12 w-full bg-slate-900 hover:bg-slate-800 text-white"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : t.save}
            </Button>
          </form>

          {/* Bulk */}
          <form onSubmit={onBulk} className="border border-slate-200 bg-white p-6 lg:p-8 space-y-5" data-testid="qa-bulk-form">
            <div className="flex items-center gap-2">
              <FileUp className="w-4 h-4 text-slate-500" />
              <p className="eyebrow">{t.bulkAdd}</p>
            </div>
            <p className="text-xs text-slate-500 leading-relaxed">
              {isAr
                ? "كل سؤال في كتلة منفصلة، افصل بين الكتل بسطر يحتوي على ---"
                : "One pair per block, separate blocks with a line containing ---"}
            </p>
            <Textarea
              required dir="auto" rows={8}
              placeholder={t.bulkPh}
              value={bulk.text}
              onChange={(e) => setBulk({ ...bulk, text: e.target.value })}
              data-testid="qa-bulk-text"
              className="rounded-sm font-mono text-[13px]"
            />
            <div>
              <Label className="text-xs uppercase tracking-[0.2em] text-slate-500">{t.language}</Label>
              <Select value={bulk.language} onValueChange={(v) => setBulk({ ...bulk, language: v })}>
                <SelectTrigger data-testid="qa-bulk-language" className="mt-2 rounded-sm h-11">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ar">{isAr ? "العربية" : "Arabic"}</SelectItem>
                  <SelectItem value="en">{isAr ? "الإنجليزية" : "English"}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button
              type="submit" disabled={bulkBusy}
              data-testid="qa-bulk-submit"
              className="rounded-sm h-12 w-full bg-slate-900 hover:bg-slate-800 text-white"
            >
              {bulkBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : t.bulkSubmit}
            </Button>
          </form>

          {/* Auto-answer (questions only) */}
          <form onSubmit={onAuto} className="border border-slate-200 bg-white p-6 lg:p-8 space-y-5" data-testid="qa-auto-form">
            <div className="flex items-center gap-2">
              <GraduationCap className="w-4 h-4 text-slate-500" />
              <p className="eyebrow">{t.autoTitle}</p>
            </div>
            <p className="text-xs text-slate-500 leading-relaxed">{t.autoLead}</p>
            <Textarea
              required dir="auto" rows={8}
              placeholder={isAr
                ? "1. ما هي شروط الزواج وفقًا لقانون الأحوال الشخصية؟\n2. متى يحق للزوجة طلب الطلاق؟\n3. ..."
                : "1. What are the requirements of marriage under Kuwaiti personal-status law?\n2. When can a wife file for divorce?\n3. ..."}
              value={auto.text}
              onChange={(e) => setAuto({ ...auto, text: e.target.value })}
              data-testid="qa-auto-text"
              className="rounded-sm font-mono text-[13px]"
            />
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs uppercase tracking-[0.2em] text-slate-500">{t.language}</Label>
                <Select value={auto.language} onValueChange={(v) => setAuto({ ...auto, language: v })}>
                  <SelectTrigger data-testid="qa-auto-language" className="mt-2 rounded-sm h-11">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ar">{isAr ? "العربية" : "Arabic"}</SelectItem>
                    <SelectItem value="en">{isAr ? "الإنجليزية" : "English"}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.2em] text-slate-500">{t.category}</Label>
                <Input
                  dir="auto"
                  placeholder={t.autoCategory}
                  value={auto.category}
                  onChange={(e) => setAuto({ ...auto, category: e.target.value })}
                  data-testid="qa-auto-category"
                  className="mt-2 rounded-sm h-11"
                />
              </div>
            </div>
            {autoProgress && (
              <div className="text-xs text-slate-500" data-testid="qa-auto-progress">
                {t.autoProgressLabel}: {autoProgress.done}/{autoProgress.total}
                <div className="mt-2 h-1 bg-slate-100">
                  <div
                    className="h-1 bg-slate-900 transition-all"
                    style={{ width: `${(autoProgress.done / autoProgress.total) * 100}%` }}
                  />
                </div>
              </div>
            )}
            <p className="text-xs text-slate-400">{t.autoBatchInfo}</p>
            <Button
              type="submit" disabled={autoBusy}
              data-testid="qa-auto-submit"
              className="rounded-sm h-12 w-full bg-slate-900 hover:bg-slate-800 text-white"
            >
              {autoBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : t.autoSubmit}
            </Button>
          </form>
        </div>

        {/* LIBRARY */}
        <div className="lg:col-span-7">
          <div className="border border-slate-200 bg-white">
            <div className="px-6 py-4 border-b border-slate-200 flex items-center gap-2">
              <BookOpenText className="w-4 h-4 text-slate-500" />
              <p className="eyebrow">
                {t.library} {items.length > 0 && <span className="ms-2 text-slate-400">· {items.length}</span>}
              </p>
            </div>
            {items.length === 0 ? (
              <div className="p-10 text-center text-slate-500 text-sm">{t.libraryEmpty}</div>
            ) : (
              <ul className="divide-y divide-slate-100 max-h-[760px] overflow-auto">
                {items.map((q) => (
                  <li key={q.id} data-testid={`qa-row-${q.id}`} className="p-6">
                    <div className="flex items-start justify-between gap-4">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <GraduationCap className="w-3.5 h-3.5 text-slate-400" />
                          <span className="text-[10px] uppercase tracking-[0.18em] text-slate-400">
                            {q.language === "ar" ? "AR" : "EN"}
                            {q.category && <> · {q.category}</>}
                          </span>
                        </div>
                        <p dir="auto" className="font-medium text-slate-900 leading-snug">{q.question}</p>
                        <p dir="auto" className="mt-2 text-sm text-slate-600 whitespace-pre-wrap leading-relaxed">{q.answer}</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => onDelete(q.id)}
                        data-testid={`qa-delete-${q.id}`}
                        className="text-slate-400 hover:text-red-600 transition-colors shrink-0"
                        aria-label={t.delete}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
